import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'

type UnknownRecord = Record<string, unknown>

const getListPayload = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord
    if (Array.isArray(record.data)) {
      return record.data as T[]
    }
    if (Array.isArray(record.results)) {
      return record.results as T[]
    }
    if (record.data && typeof record.data === 'object' && Array.isArray((record.data as UnknownRecord).results)) {
      return (record.data as UnknownRecord).results as T[]
    }
  }

  return []
}

const getObjectPayload = <T>(payload: unknown): T | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const record = payload as UnknownRecord
  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    return record.data as T
  }

  return record as T
}

export interface OrgWalletVariant {
  id: string | number
  name?: string | null
  type?: number | null
  type_name?: string | null
  pg_type?: number | null
  active?: boolean | null
}

export interface OrgWallet {
  id: string | number
  org_instance?: string | number | null
  node?: string | number | null
  wallet_variant?: OrgWalletVariant | string | number | null
  acc_name?: string | null
  acc_num?: string | null
  code?: string | null
  branch?: string | null
  contact?: string | null
  international_code?: string | null
  acc_routing_swift_ifsc?: string | null
  api_key?: string | null
  api_server?: string | null
  api_url?: string | null
  environment?: string | null
  balance?: string | number | null
  currency?: string | number | null
  active?: boolean | null
  wallet_name?: string | null
  label?: string | null
  is_mobile_enabled?: boolean | null
  is_web_enabled?: boolean | null
  is_pos_enabled?: boolean | null
}

export interface OrgWalletPayload {
  org_instance: string
  node: string | number
  wallet_variant: string | number
  label?: string
  acc_name?: string
  acc_num?: string
  code?: string
  branch?: string
  contact?: string
  api_key?: string
  api_server?: string
  api_url?: string
  environment?: string
  is_mobile_enabled?: boolean
  is_web_enabled?: boolean
  is_pos_enabled?: boolean
}

const normalizeWallet = (wallet: Partial<OrgWallet> | null | undefined): OrgWallet => ({
  id: String(wallet?.id ?? ''),
  org_instance: wallet?.org_instance ?? null,
  node: wallet?.node ?? null,
  wallet_variant: wallet?.wallet_variant ?? null,
  acc_name: wallet?.acc_name ?? null,
  acc_num: wallet?.acc_num ?? null,
  code: wallet?.code ?? null,
  branch: wallet?.branch ?? null,
  contact: wallet?.contact ?? null,
  international_code: wallet?.international_code ?? null,
  acc_routing_swift_ifsc: wallet?.acc_routing_swift_ifsc ?? null,
  api_key: wallet?.api_key ?? null,
  api_server: wallet?.api_server ?? null,
  api_url: wallet?.api_url ?? null,
  environment: wallet?.environment ?? null,
  balance: wallet?.balance ?? null,
  currency: wallet?.currency ?? null,
  active: wallet?.active ?? null,
  wallet_name: wallet?.wallet_name ?? null,
  label: wallet?.label ?? null,
  is_mobile_enabled: wallet?.is_mobile_enabled ?? true,
  is_web_enabled: wallet?.is_web_enabled ?? true,
  is_pos_enabled: wallet?.is_pos_enabled ?? true,
})

const normalizeVariant = (variant: Partial<OrgWalletVariant> | null | undefined): OrgWalletVariant => ({
  id: String(variant?.id ?? ''),
  name: variant?.name ?? null,
  type: variant?.type ?? null,
  type_name: variant?.type_name ?? null,
  pg_type: variant?.pg_type ?? null,
  active: variant?.active ?? null,
})

const normalizePayload = (payload: OrgWalletPayload) => {
  const normalizedEntries = Object.entries(payload).filter(([, value]) => {
    if (value == null) {
      return false
    }

    if (typeof value === 'string') {
      return value.trim().length > 0
    }

    return true
  })

  return Object.fromEntries(normalizedEntries)
}

export const getOrgWalletLabel = (wallet: OrgWallet) => {
  if (wallet.label?.trim()) {
    return wallet.label.trim()
  }

  if (wallet.wallet_name?.trim()) {
    return wallet.wallet_name.trim()
  }

  if (wallet.acc_name?.trim()) {
    return wallet.acc_name.trim()
  }

  if (wallet.wallet_variant && typeof wallet.wallet_variant === 'object') {
    const variant = wallet.wallet_variant
    if (variant.type_name?.trim()) {
      return variant.type_name.trim()
    }
    if (variant.name?.trim()) {
      return variant.name.trim()
    }
  }

  return wallet.code?.trim() || `Wallet ${wallet.id}`
}

export const orgWalletApi = {
  listWallets: async (
    serviceInstanceId: string | number,
    options?: {
      nodeId?: string | number
      orgInstanceId?: string | number
      /** When true, only wallets owned by this service instance (no parent/ancestor fallback). */
      exactScope?: boolean
      /** Wallet variant type, e.g. 3 for Bank. */
      type?: string | number
    },
  ): Promise<OrgWallet[]> => {
    const response = await api.get(API_PATHS.orgWallet.wallets.list, {
      params: {
        service_instance_id: serviceInstanceId,
        node: options?.nodeId,
        org_instance: options?.orgInstanceId,
        ...(options?.exactScope ? { exact_scope: true } : {}),
        ...(options?.type != null ? { type: options.type } : {}),
        page_size: 100,
      },
    })

    return getListPayload<OrgWallet>(response.data).map(normalizeWallet)
  },

  getWallet: async (walletId: string | number): Promise<OrgWallet | null> => {
    const response = await api.get(API_PATHS.orgWallet.wallets.detail(walletId))

    const direct = getObjectPayload<OrgWallet>(response.data)
    if (direct && (direct.id != null || direct.wallet_variant != null)) {
      return normalizeWallet(direct)
    }

    return getListPayload<OrgWallet>(response.data).map(normalizeWallet)[0] ?? null
  },

  createWallet: async (payload: OrgWalletPayload) => {
    const response = await api.post(API_PATHS.orgWallet.wallets.create, normalizePayload(payload))
    return normalizeWallet(getObjectPayload<OrgWallet>(response.data))
  },

  updateWallet: async (walletId: string | number, payload: Partial<OrgWalletPayload>) => {
    const response = await api.patch(API_PATHS.orgWallet.wallets.update(walletId), normalizePayload(payload as OrgWalletPayload))
    return normalizeWallet(getObjectPayload<OrgWallet>(response.data))
  },

  deleteWallet: async (walletId: string | number) => {
    const response = await api.delete(API_PATHS.orgWallet.wallets.delete(walletId))
    return response.data
  },

  listWalletVariants: async (): Promise<OrgWalletVariant[]> => {
    const response = await api.get(API_PATHS.orgWallet.variants.list, {
      params: {
        page_size: 100,
      },
    })

    return getListPayload<OrgWalletVariant>(response.data).map(normalizeVariant)
  },
}